import React, { useEffect, useState, useCallback, useContext } from "react";
import { InputSelect } from "@bookingcom/bui-react";
import { format as formatDate } from "date-fns";
import useCalendarSelect from "./useCalendarSelect.js";
import { t } from "@bookingcom/lingojs-core";
import { useFareCalendarEnabled, useFareCalendar } from "../FareCalendar/useFareCalendar.js";
import FareCalendarLegend from "../FareCalendar/FareCalendarLegend.js";
import { useSearchboxStore } from "../../store/searchBoxStore.js";
import { SearchBoxContext } from "../SearchBoxContext/index.js";
import styles from "./CalendarSelect.module.css";
export default function CalendarSelect(props) {
    const { segmentIndex, onDateChange, variant = "vertical" } = props;
    const store = useSearchboxStore();
    const { fetchedRanges, legend, data: fareCalendarData } = store.searchbox.fareCalendar;
    const { isMobile, fareCalendarConfig } = useContext(SearchBoxContext);
    const mode = isMobile ? "vertical" : "desktop";
    const fareCalendarEnabled = useFareCalendarEnabled(segmentIndex, fareCalendarConfig, mode);
    const [currentMonth, setCurrentMonth] = useState(null);
    const { dateValues, fromDay, fromYearMonth, fromYearMonthList, getDaysList, i18n, isRoundTrip, onFromDayChange, onFromYearMonthChange, onToDayChange, onToYearMonthChange, toDay, toYearMonth, toYearMonthList, startDate } = useCalendarSelect({ segmentIndex });
    const singleMonth = isRoundTrip && fromYearMonth === toYearMonth;
    const { handleMonthChange, loading } = useFareCalendar({
        baseDate: startDate || undefined,
        mode: isMobile ? "vertical" : "desktop",
        enabled: fareCalendarEnabled,
        segmentIndex,
        singleMonth,
        disableAutoFetch: true
    });
    const getPriceIndicationDots = useCallback((priceIndication) => {
        switch (priceIndication) {
            case "low":
                return " •";
            case "medium":
                return " ••";
            case "high":
                return " •••";
            default:
                return "";
        }
    }, []);
    const enhanceDaysListWithPriceIndication = useCallback((daysList, yearMonth) => {
        if (!fareCalendarEnabled || !yearMonth)
            return daysList;
        return daysList.map((dayOption) => {
            if (!dayOption.value || dayOption.disabled)
                return dayOption;
            const [year, month] = yearMonth.split("-").map(Number);
            const day = Number(dayOption.value);
            const date = new Date(year, month, day);
            const dateKey = formatDate(date, "yyyy-MM-dd");
            const priceData = fareCalendarData[dateKey];
            if (priceData?.priceIndication) {
                return {
                    ...dayOption,
                    text: dayOption.text + getPriceIndicationDots(priceData.priceIndication)
                };
            }
            const dateInFetchedRange = fetchedRanges.some((range) => {
                const rangeStart = new Date(range.start);
                const rangeEnd = new Date(range.end);
                return date >= rangeStart && date <= rangeEnd;
            });
            if (dateInFetchedRange) {
                return {
                    ...dayOption,
                    text: dayOption.text + " -"
                };
            }
            return dayOption;
        });
    }, [fareCalendarEnabled, fareCalendarData, getPriceIndicationDots, fetchedRanges]);
    const getCurrentLegend = useCallback(() => {
        if (!currentMonth)
            return legend;
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth() + 1;
        const matchingRange = fetchedRanges.find((range) => {
            const rangeStart = new Date(range.start);
            return rangeStart.getFullYear() === year && rangeStart.getMonth() === month - 1;
        });
        return matchingRange?.legend || legend;
    }, [currentMonth, fetchedRanges, legend]);
    useEffect(() => {
        onDateChange(dateValues);
    }, [dateValues, onDateChange]);
    useEffect(() => {
        const shouldTrigger = isRoundTrip ? fromYearMonth && toYearMonth : fromYearMonth;
        if (shouldTrigger && fromYearMonth) {
            const [year, month] = fromYearMonth.split("-").map(Number);
            const monthDate = new Date(year, month, 1);
            setCurrentMonth(monthDate);
            handleMonthChange(monthDate);
        }
    }, [fromYearMonth, toYearMonth, isRoundTrip, handleMonthChange, singleMonth]);
    const defaultOption = { text: "-", value: "", disabled: true };
    return (React.createElement(React.Fragment, null,
        (isRoundTrip ? fromYearMonth && toYearMonth : fromYearMonth) &&
            ((isMobile && fareCalendarConfig.isFareCalendarMobileEnabled) ||
                (!isMobile && fareCalendarConfig.isFareCalendarDesktopEnabled)) &&
            fareCalendarEnabled ? (React.createElement("div", { className: styles.legendContainer },
            React.createElement(FareCalendarLegend, { text: "Price indication based on average lowest price in next 60 days", low: getCurrentLegend().low, medium: getCurrentLegend().medium, high: getCurrentLegend().high, loading: loading, addContainer: false }))) : null,
        React.createElement("div", { className: `${styles.wrapper} ${variant === "horizontal" ? styles.wrapperHorizontal : ""}` },
            React.createElement(InputSelect, { label: i18n.trans(t("flights_index_search_box_departure_month")), name: `searchbox-date-from-month-year-${segmentIndex}`, options: [defaultOption, ...fromYearMonthList], value: fromYearMonth, onChange: ({ value }) => {
                    onFromYearMonthChange(value);
                } }),
            React.createElement(InputSelect, { label: i18n.trans(t("flights_index_search_box_departure_date")), name: `searchbox-date-from-day-${segmentIndex}`, options: [defaultOption, ...enhanceDaysListWithPriceIndication(getDaysList(fromYearMonth), fromYearMonth)], disabled: !fromYearMonth, value: fromDay, onChange: ({ value }) => {
                    onFromDayChange(value);
                } }),
            isRoundTrip ? (React.createElement(React.Fragment, null,
                React.createElement(InputSelect, { label: i18n.trans(t("flights_index_search_box_return_month")), name: `searchbox-date-to-month-year-${segmentIndex}`, options: [defaultOption, ...toYearMonthList], value: toYearMonth, disabled: !fromDay, onChange: ({ value }) => {
                        onToYearMonthChange(value);
                    } }),
                React.createElement(InputSelect, { label: i18n.trans(t("flights_index_search_box_return_date")), name: `searchbox-date-to-day-${segmentIndex}`, options: [
                        defaultOption,
                        ...enhanceDaysListWithPriceIndication(getDaysList(toYearMonth, dateValues.from), toYearMonth)
                    ], disabled: !toYearMonth, value: toDay, onChange: ({ value }) => {
                        onToDayChange(value);
                    } }))) : null)));
}
